import { Injectable } from '@angular/core';
import { ENDPOINTS, LoadStatus, OnlineOnlyService } from '@evorait/pace-core';
import { Store } from '@ngrx/store';
import { filter, map, Observable, of, switchMap, take, throwError } from 'rxjs';
import { MeasurementTools, ODataMeasurementTools } from '../types/measurement-tools';
import { loadODataMeasurementTools } from '../store/actions/measurement-tools.action';
import { getMeasurementToolsStatus } from '../store/selectors/measurement-tools.selector';

@Injectable({
    providedIn: 'root',
})
export class MeasurementToolsService {
    public constructor(
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly store: Store,
    ) {}

    /**
     * Trigger the load effect and resolve once the slice settles: complete on success, or error
     * on failure. Mirrors ConfigService/ValueHelperService's init() so it can be joined into the
     * same startup forkJoin.
     */
    public init(): Observable<void> {
        this.store.dispatch(loadODataMeasurementTools());
        return this.store.select(getMeasurementToolsStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            switchMap((status) =>
                status === LoadStatus.Error
                    ? throwError(() => new Error('MeasurementToolsService: init failed'))
                    : of(undefined as void),
            ),
        );
    }

    public getMeasurementTools(): Observable<MeasurementTools[]> {
        return (
            this.onlineOnlyService.get<ODataMeasurementTools>(ENDPOINTS.MeasurementEquipsSet) as Observable<
                ODataMeasurementTools[]
            >
        ).pipe(map((data) => this.mapODataMeasurementToolsToState(data)));
    }

    private mapODataMeasurementToolsToState(oDataMeasurementTools: ODataMeasurementTools[]): MeasurementTools[] {
        return oDataMeasurementTools.map((item) => ({
            equipmentId: item.EquipmentNumber,
            description: item.EquipmentDescription,
            calibratedDate: item.NextCheckDate,
        }));
    }
}
